import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient, type Query } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { SearchParam } from '@common/ts/enums';
import useTranslation from '@/hooks/useTranslation';
import { isFilterNotFoundError } from '@/utils/filterNotFound';

// Matched on the JSON-quoted id so only a key segment that IS this filter id
// counts, never an id that merely contains it.
const isForFilter = (query: Query, filterId: string) =>
  JSON.stringify(query.queryKey).includes(JSON.stringify(filterId));

/**
 * TELV1-606 — the results page's filter lives in `?filter_id`, but that id can
 * outlive the filter: an old bookmark or shared link, another tab that deleted
 * it, or the filter restored from localStorage after a re-run. The analyzer
 * then answers 404 "Filter not found" on every filtered query (transcripts,
 * exports, live statistics, run state, RA suggestions).
 *
 * On the first such error for the CURRENT filter this drops the filter from the
 * URL and from the re-run restore key, and says so once; every query re-keys
 * to "no filter" and loads. Errors are matched to the current filter by query
 * key so a leftover failed query from an earlier filter cannot clear a valid one.
 */
export function useStaleFilterRecovery(projectId: string | undefined) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { lang } = useTranslation();
  const filterId = searchParams.get(SearchParam.FILTER_ID);

  useEffect(() => {
    if (!filterId) return undefined;

    let dropped = false;
    const dropStaleFilter = () => {
      if (dropped) return;
      dropped = true;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(SearchParam.FILTER_ID);
          return next;
        },
        { replace: true },
      );
      // Only forget the re-run restore value if it is this stale filter; a
      // different, still-valid filter saved there must survive.
      const rerunFilterKey = projectId ? `rerun_state_${projectId}_filter_id` : null;
      if (rerunFilterKey && localStorage.getItem(rerunFilterKey) === filterId) {
        localStorage.removeItem(rerunFilterKey);
      }
      toast.info(lang.get('msg.filterNoLongerExists'), {
        toastId: `filter-not-found-${filterId}`,
      });
    };

    const cache = queryClient.getQueryCache();
    // A query may have failed before this effect subscribed.
    const alreadyFailed = cache
      .getAll()
      .some(
        (query) =>
          query.state.status === 'error' &&
          isForFilter(query, filterId) &&
          isFilterNotFoundError(query.state.error),
      );
    if (alreadyFailed) {
      dropStaleFilter();
      return undefined;
    }

    return cache.subscribe((event) => {
      if (
        event.type === 'updated' &&
        event.action.type === 'error' &&
        isForFilter(event.query, filterId) &&
        isFilterNotFoundError(event.action.error)
      ) {
        dropStaleFilter();
      }
    });
  }, [filterId, projectId, queryClient, setSearchParams, lang]);
}
